/**
 * <flavor-picker>
 *
 * A grid (or mobile carousel) of products shown inside the main product
 * section. The product of the current page is pre-selected; selecting any card
 * reveals its quantity stepper. On "Add to cart" every selected product is
 * added in a single /cart/add request with its own quantity.
 */
(function () {
  if (customElements.get('flavor-picker')) return;

  /** Every picker on the page. */
  function allPickers() {
    return Array.from(document.querySelectorAll('flavor-picker'));
  }

  /**
   * The picker that owns `form` (matches the main form or its "--alt" sticky clone).
   *
   * `form.getAttribute('id')`, never `form.id`: the product form contains an
   * <input name="id">, and named form controls shadow same-named properties on
   * the form element, so `form.id` returns that input rather than the id string.
   */
  function pickerForForm(form) {
    if (!form) return null;
    const formId = form.getAttribute('id');
    if (!formId) return null;
    const baseId = formId.replace(/--alt$/, '');
    return allPickers().find((picker) => picker.dataset.formId === baseId) || null;
  }

  /**
   * The theme's <product-form> posts a single variant. Whenever a picker owns
   * the form we take over before that happens. Intercepting the click as well
   * as the submit means we win regardless of listener registration order.
   */
  function intercept(event, form) {
    const picker = pickerForForm(form);
    if (!picker || typeof picker.getSelection !== 'function') return;
    if (picker.getSelection().length === 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    picker.addToCart(form);
  }

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('button[type="submit"][name="add"], [type="submit"][name="add"]');
      if (!button || button.disabled) return;
      intercept(event, button.form || button.closest('form'));
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      if (event.target instanceof HTMLFormElement) intercept(event, event.target);
    },
    true
  );

  customElements.define(
    'flavor-picker',
    class FlavorPicker extends HTMLElement {
      connectedCallback() {
        this.formId = this.dataset.formId;
        this.allowMultiple = this.dataset.allowMultiple !== 'false';
        this.requireSelection = this.dataset.requireSelection !== 'false';
        this.maxQuantity = parseInt(this.dataset.maxQuantity, 10) || 99;
        this.inputsContainer = this.querySelector('.flavor-picker__inputs');

        // The picker sits deep inside main-product, and an ancestor there
        // creates a stacking context — so however high the modal's z-index is,
        // it still paints under the header. Re-parenting to <body> escapes it.
        if (!this.infoModal) {
          const modal = this.querySelector('[data-flavor-info-modal]');
          if (modal) {
            document.body.appendChild(modal);
            this.infoModal = modal;
            // Close clicks no longer bubble to the picker, so listen here.
            modal.addEventListener('click', (event) => {
              if (event.target.closest('[data-flavor-info-close]')) {
                event.preventDefault();
                this.closeInfo();
              }
            });
          }
        }

        if (!this.listenersBound) {
          this.listenersBound = true;
          this.addEventListener('click', this.onClick.bind(this));
          this.addEventListener('change', this.onChange.bind(this));

          // Start loading before the click lands. pointerover fires on hover
          // (desktop); pointerdown covers touch, where there is no hover.
          const warm = (event) => {
            const button = event.target.closest?.('[data-flavor-info]');
            if (button) this.prefetchInfo(button);
          };
          this.addEventListener('pointerover', warm);
          this.addEventListener('pointerdown', warm);
          this.addEventListener('focusin', warm);

          this.escHandler = (event) => {
            if (event.key === 'Escape') this.closeInfo();
          };
          document.addEventListener('keydown', this.escHandler);
        }

        // The buy buttons render after this block, so wait for the document
        // before touching the form.
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.syncState(), { once: true });
        } else {
          this.syncState();
        }
      }

      disconnectedCallback() {
        this.infoModal?.remove();
        this.infoModal = null;
        if (this.escHandler) document.removeEventListener('keydown', this.escHandler);
        this.listenersBound = false;
      }

      /** Every form this picker feeds: the main product form and the sticky "--alt" clone. */
      getForms() {
        if (!this.formId) return [];
        return [document.getElementById(this.formId), document.getElementById(`${this.formId}--alt`)].filter(Boolean);
      }

      get items() {
        return Array.from(this.querySelectorAll('[data-flavor-item]'));
      }

      /**
       * Fetches (and caches) a flavor-info view. Returns the same promise for
       * concurrent callers so hovering then clicking does not fetch twice.
       */
      static loadInfo(url) {
        FlavorPicker.infoCache = FlavorPicker.infoCache || new Map();
        FlavorPicker.infoPending = FlavorPicker.infoPending || new Map();

        if (FlavorPicker.infoCache.has(url)) return Promise.resolve(FlavorPicker.infoCache.get(url));
        if (FlavorPicker.infoPending.has(url)) return FlavorPicker.infoPending.get(url);

        const request = fetch(url)
          .then((response) => {
            if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
            return response.text();
          })
          .then((text) => {
            const main = new DOMParser().parseFromString(text, 'text/html').querySelector('main');
            if (!main) throw new Error('flavor-info view returned no content');
            FlavorPicker.infoCache.set(url, main.innerHTML);
            return main.innerHTML;
          })
          .finally(() => FlavorPicker.infoPending.delete(url));

        FlavorPicker.infoPending.set(url, request);
        return request;
      }

      /** Warms the cache before the click lands. */
      prefetchInfo(button) {
        const url = button?.dataset.flavorInfoUrl;
        if (url) FlavorPicker.loadInfo(url).catch(() => {});
      }

      /**
       * Opens the product's `flavor-info` view — the full product view minus the
       * purchase controls, loaded the same way the theme's quick view loads.
       */
      async openInfo(button) {
        const modal = this.infoModal;
        const body = modal?.querySelector('[data-flavor-info-body]');
        const loader = modal?.querySelector('[data-flavor-info-loader]');
        const url = button?.dataset.flavorInfoUrl;
        if (!modal || !body || !url) return;

        modal.hidden = false;
        document.body.classList.add('quick-view-open');
        document.body.style.overflow = 'hidden';
        modal.querySelector('[data-flavor-info-close]')?.focus();
        this.openInfoUrl = url;

        const cached = FlavorPicker.infoCache?.get(url);
        if (cached) {
          if (loader) loader.hidden = true;
          this.renderInfo(body, cached);
          return;
        }

        body.innerHTML = '';
        // Only flash a spinner if the fetch is actually slow — a warm or fast
        // response should just appear.
        if (loader) loader.hidden = true;
        const spinnerTimer = setTimeout(() => {
          if (loader && !modal.hidden && this.openInfoUrl === url) loader.hidden = false;
        }, 200);

        try {
          const html = await FlavorPicker.loadInfo(url);
          clearTimeout(spinnerTimer);
          if (loader) loader.hidden = true;
          // Bail if the shopper closed the dialog or opened another flavour.
          if (modal.hidden || this.openInfoUrl !== url) return;
          this.renderInfo(body, html);
        } catch (error) {
          clearTimeout(spinnerTimer);
          if (loader) loader.hidden = true;
          body.innerHTML = `<p class="flavor-info__error">${
            button.dataset.flavorInfoTitle || ''
          }</p><p class="flavor-info__error"><a href="${url.split('?')[0]}">View full details</a></p>`;
          console.error('[flavor-picker]', error);
        }
      }

      /** Injects markup and re-runs its scripts, which innerHTML disables. */
      renderInfo(body, html) {
        body.innerHTML = html;

        body.querySelectorAll('script').forEach((oldScript) => {
          const script = document.createElement('script');
          Array.from(oldScript.attributes).forEach((attr) => script.setAttribute(attr.name, attr.value));
          script.appendChild(document.createTextNode(oldScript.innerHTML));
          oldScript.parentNode.replaceChild(script, oldScript);
        });

        if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
        body.closest('.flavor-info__dialog')?.scrollTo(0, 0);
      }

      closeInfo() {
        const modal = this.infoModal;
        if (!modal || modal.hidden) return;
        modal.hidden = true;
        this.openInfoUrl = null;
        document.body.classList.remove('quick-view-open');
        document.body.style.overflow = '';
      }

      onClick(event) {
        const infoButton = event.target.closest('[data-flavor-info]');
        if (infoButton) {
          // Must not fall through to the card's select overlay.
          event.preventDefault();
          event.stopPropagation();
          this.openInfo(infoButton);
          return;
        }

        const stepButton = event.target.closest('[data-flavor-step]');
        if (stepButton) {
          event.preventDefault();
          this.step(stepButton.closest('[data-flavor-item]'), parseInt(stepButton.dataset.flavorStep, 10));
          return;
        }

        const toggle = event.target.closest('[data-flavor-toggle]');
        if (toggle && !toggle.disabled) {
          event.preventDefault();
          this.toggle(toggle.closest('[data-flavor-item]'));
        }
      }

      onChange(event) {
        const input = event.target.closest('[data-flavor-quantity-input]');
        if (!input) return;
        const item = input.closest('[data-flavor-item]');
        input.value = this.clamp(parseInt(input.value, 10));
        this.updateStepButtons(item);
        this.syncState();
      }

      clamp(value) {
        if (!Number.isFinite(value) || value < 1) return 1;
        return Math.min(value, this.maxQuantity);
      }

      toggle(item) {
        if (!item) return;
        const willSelect = !item.classList.contains('is-selected');

        if (willSelect && !this.allowMultiple) {
          this.items.forEach((other) => {
            other.classList.remove('is-selected');
            other.querySelector('[data-flavor-toggle]')?.setAttribute('aria-pressed', 'false');
          });
        }

        item.classList.toggle('is-selected', willSelect);
        item.querySelector('[data-flavor-toggle]')?.setAttribute('aria-pressed', String(willSelect));
        this.updateStepButtons(item);
        this.syncState();
      }

      step(item, delta) {
        if (!item) return;
        const input = item.querySelector('[data-flavor-quantity-input]');
        if (!input) return;
        input.value = this.clamp((parseInt(input.value, 10) || 1) + delta);
        this.updateStepButtons(item);
        this.syncState();
      }

      updateStepButtons(item) {
        const input = item?.querySelector('[data-flavor-quantity-input]');
        if (!input) return;
        const value = parseInt(input.value, 10) || 1;
        const minus = item.querySelector('[data-flavor-step="-1"]');
        const plus = item.querySelector('[data-flavor-step="1"]');
        if (minus) minus.disabled = value <= 1;
        if (plus) plus.disabled = value >= this.maxQuantity;
      }

      /** [{ id: variantId, quantity: n }] for every selected, available card. */
      getSelection() {
        return this.items
          .filter((item) => item.classList.contains('is-selected') && item.dataset.available === 'true')
          .map((item) => {
            const input = item.querySelector('[data-flavor-quantity-input]');
            return {
              id: item.dataset.variantId,
              quantity: this.clamp(parseInt(input?.value, 10) || 1),
            };
          })
          .filter((line) => line.id);
      }

      /** Mirrors the selection into hidden `items[n][…]` inputs and toggles the submit button. */
      syncState() {
        const selection = this.getSelection();

        if (this.inputsContainer) {
          this.inputsContainer.innerHTML = selection
            .map(
              (line, index) =>
                `<input type="hidden" name="items[${index}][id]" value="${line.id}">` +
                `<input type="hidden" name="items[${index}][quantity]" value="${line.quantity}">`
            )
            .join('');
        }

        this.items.forEach((item) => this.updateStepButtons(item));

        this.getForms().forEach((form) => {
          if (this.dataset.hideDynamicCheckout !== 'false') {
            form.classList.add('flavor-picker-active');
          }
          if (!this.requireSelection) return;
          const submit = form.querySelector('[type="submit"][name="add"]');
          if (!submit || submit.hasAttribute('data-sold-out')) return;
          submit.disabled = selection.length === 0;
        });

        this.dispatchEvent(new CustomEvent('flavor:change', { bubbles: true, detail: { selection } }));
      }

      showError(message) {
        const wrapper = document.querySelector('.product-form__error-message-wrapper');
        const target = wrapper?.querySelector('.product-form__error-message');
        if (wrapper && target) {
          target.textContent = message;
          wrapper.hidden = false;
        } else {
          console.error('[flavor-picker]', message);
        }
      }

      /** Adds every selected line item, then refreshes the cart drawer / notification. */
      async addToCart(form) {
        const selection = this.getSelection();
        if (selection.length === 0) return;

        const submitButton = form.querySelector('[type="submit"][name="add"]');
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const wrapper = form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
        if (wrapper) wrapper.hidden = true;

        submitButton?.setAttribute('aria-disabled', 'true');
        submitButton?.classList.add('loading');
        submitButton?.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

        // Carry any line item properties the merchant added through other blocks.
        const properties = {};
        new FormData(form).forEach((value, key) => {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && value !== '') properties[match[1]] = value;
        });
        const hasProperties = Object.keys(properties).length > 0;

        const body = {
          items: selection.map((line) => {
            const item = { id: Number(line.id), quantity: line.quantity };
            if (hasProperties) item.properties = properties;
            return item;
          }),
        };

        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement?.(document.activeElement);
        }

        const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

        try {
          const response = await fetch(addUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const data = await response.json();

          if (!response.ok || data.status) {
            this.showError(data.description || data.message || 'Could not add these products to the cart.');
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'flavor-picker',
                errors: data.description,
                message: data.message,
              });
            }
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'flavor-picker' });
          }

          if (!cart) {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
            return;
          }

          cart.renderContents(data);
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        } catch (error) {
          this.showError('Could not add these products to the cart.');
          console.error('[flavor-picker]', error);
        } finally {
          submitButton?.classList.remove('loading');
          submitButton?.removeAttribute('aria-disabled');
          submitButton?.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
        }
      }
    }
  );
})();
